import { State } from 'src/replicant/State';
import {
  BundleSaleProductId,
  bundleSaleEvents,
  bundleSaleDefaultWeek,
  bundleSaleRewards,
  bundleSaleSchedule,
  match2BundleSaleUnlockLevel,
} from './match2-bundlesale.ruleset';
import { processMatch2Rewards } from '../rewards/match2-rewards.getters';
import { getJSTDateTime } from 'src/replicant/utils/time';

/**
 * Find if the feature is enabled, regardless if there are events or not
 */
export function match2BundleSaleIsActive(state: State): boolean {
  return true;
}

/**
 * Check if bundle sale feature is active and if there is an ongoing event.
 */
export function match2BundleSaleIsAvailable(
  state: State,
  now: number,
): boolean {
  // Not available if the feature is not active
  if (!match2BundleSaleIsActive(state)) return false;

  // Only available if player is level 20 or higher, because the last booster (pinwheel) unlocks at level 19
  if (state.match2.level < match2BundleSaleUnlockLevel) return false;

  // Get current ongoing event
  const event = match2BundleSaleGetCurrentEvent(now);

  return !!event;
}

/**
 * Check if product is a free
 */
export function match2BundleSaleIsFreeProduct(
  id: BundleSaleProductId,
): boolean {
  return bundleSaleRewards[id].free;
}

/**
 * Get reimbursable bundle sale products for a paid product.
 * Returns purchased product itself + subsequent free products
 * until the next paid product (or end of event).
 */
export function match2BundleSaleGetReimbursableProducts(
  productId: BundleSaleProductId,
) {
  for (const event of bundleSaleEvents) {
    const index = event.items.indexOf(productId);
    if (index === -1) continue;

    const reimbursableProducts: BundleSaleProductId[] = [productId];
    for (let i = index + 1; i < event.items.length; i++) {
      const nextProductId = event.items[i];
      if (!match2BundleSaleIsFreeProduct(nextProductId)) {
        break;
      }
      reimbursableProducts.push(nextProductId);
    }

    return reimbursableProducts;
  }

  return [];
}

/**
 * Get the Monday of the week, in the format 'yyyy-MM-dd'
 */
export function match2BundleSaleGetMonday(now: number): string {
  const date = getJSTDateTime(now);
  const day = date.weekday;
  const diff = day === 0 ? 6 : day - 1; // If the day is Sunday (0), the difference is 6. Otherwise, it's day - 1.
  return date.minus({ days: diff }).toFormat('yyyy-MM-dd');
}

/**
 * Get ongoing schedule, if any
 */
export function match2BundleSaleGetCurrentSchedule(now: number) {
  const event = bundleSaleSchedule.getCurrentEvent(now);

  if (!event) return null;
  if (!event.metadata) return null;

  return {
    theme: event.metadata.theme,
    event: event.metadata.event,
    startTime: event.start,
    endTime: event.end,
  };
}

/**
 * Get week params for the current week
 */
function match2BundleSaleGetWeek(now: number) {
  return match2BundleSaleGetCurrentSchedule(now);
}

/**
 * Get ongoing bundle sale event, if any
 */
export function match2BundleSaleGetCurrentEvent(now: number) {
  const schedule = match2BundleSaleGetCurrentSchedule(now);
  if (!schedule) return null;
  return schedule.event;
}

/**
 * Check if there are any ongoing bundle sale event
 */
export function match2BundleSaleHasCurrentEvent(now: number): boolean {
  const event = match2BundleSaleGetCurrentEvent(now);
  return !!event;
}

/**
 * retrieve products for the current event, according to the ab test bucket set
 */
export function match2BundleSaleGetCurrentEventItems(
  state: State,
  now: number,
) {
  const event = match2BundleSaleGetCurrentEvent(now);
  const items = event?.items;
  return items ?? [];
}

/**
 * Get a list of all items already purchased
 */
export function match2BundleSaleGetPurchasedItems(state: State, now: number) {
  const eventStartAt = match2BundleSaleGetCurrentSchedule(now)?.startTime ?? 0;
  const lastPurchaseAt = state.match2.bundleSale?.lastPurchasedAt ?? 0;

  // If the last purchase was before the current event start, the existing list
  // is from the previous event, so return an empty list instead
  if (lastPurchaseAt < eventStartAt) {
    return [] as BundleSaleProductId[];
  }

  return (state.match2.bundleSale?.purchased || []) as BundleSaleProductId[];
}

/**
 * Get reward set from a product pack
 */
export function match2BundleSaleGetPackRewardSet(
  id: BundleSaleProductId,
  now: number,
) {
  const pack = bundleSaleRewards[id];
  if (!pack) return [];
  const rewards = pack.rewards.filter(
    (reward) =>
      // Skip paid gems as they are handled separately
      reward.type !== 'gems' || pack.free,
  );
  return processMatch2Rewards(rewards, now);
}

/**
 * Get remaining time for the current event
 */
export function match2BundleSaleGetRemainingTime(now: number): number {
  const event = match2BundleSaleGetCurrentSchedule(now);
  if (!event) return 0;
  const remainingTime = event.endTime - now;
  return remainingTime > 0 ? remainingTime : 0;
}

/**
 * Retrieves the theme of the current bundle sale event, if there is one.
 */
export function getBundleSaleTheme(
  now: number,
): 'default' | 'xmas' | 'newyear' {
  return match2BundleSaleGetWeek(now)?.theme ?? bundleSaleDefaultWeek.theme;
}
